import { Activity, ArrowRight, FolderPlus, Inbox, Mail, Send, Users } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Page, PageHeader } from '../components/layout/AppShell';
import { Sparkline, StatCard } from '../components/ui/data';
import { Badge, Button, Card, CardBody, CardHeader, EmptyState, ErrorBlock, LoadingBlock, Progress } from '../components/ui/primitives';
import { useActivity, useProjects, useStats } from '../hooks/queries';
import { formatNumber, formatPercent, formatRelative, initials, titleCase } from '../lib/utils';

const FUNNEL = [
  { status: 'NEW', label: 'New' },
  { status: 'ENRICHED', label: 'Enriched' },
  { status: 'QUEUED', label: 'Queued' },
  { status: 'DRAFTED', label: 'Drafted' },
  { status: 'SCHEDULED', label: 'Scheduled' },
  { status: 'CONTACTED', label: 'Contacted' },
  { status: 'REPLIED', label: 'Replied' },
  { status: 'INTERESTED', label: 'Interested' },
  { status: 'WON', label: 'Won' },
];

export const DashboardPage = () => {
  const { data: stats, isLoading, error } = useStats(undefined, 20_000);
  const { data: projects, isLoading: projectsLoading, error: projectsError } = useProjects();
  const { data: activity, error: activityError } = useActivity(undefined, 12);
  const navigate = useNavigate();

  if (isLoading) return <LoadingBlock />;
  if (error) return <Page><ErrorBlock error={error} /></Page>;
  if (!stats) return null;

  const maxFunnel = Math.max(1, ...FUNNEL.map((step) => stats.leadsByStatus[step.status] ?? 0));
  const activeProjects = (projects ?? []).filter((project) => project.status === 'ACTIVE');
  const displayProjects = [...(projects ?? [])].sort((a, b) =>
    Number(b.status === 'ACTIVE') - Number(a.status === 'ACTIVE') || Date.parse(b.updatedAt) - Date.parse(a.updatedAt),
  ).slice(0, 4);

  return (
    <Page>
      <PageHeader title="Workspace overview" description="Keep your projects moving. Review progress and pick up your next task."
        actions={<Button icon={<FolderPlus className="h-4 w-4" />} onClick={() => navigate('/projects?new=1')}>New project</Button>} />

      {!projectsLoading && !projectsError && projects?.length === 0 ? (
        <Card className="mb-6">
          <EmptyState icon={<FolderPlus className="h-6 w-6" />} title="Start with your first project"
            description="Choose outreach to find and contact leads, or content to plan and publish blog posts. You can add your prompts as you go."
            action={<Button variant="primary" onClick={() => navigate('/projects?new=1')}>Create a project <ArrowRight className="h-4 w-4" /></Button>} />
        </Card>
      ) : null}

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4 sm:gap-4">
        <StatCard label="Total leads" value={formatNumber(stats.totals.leads)} sub="Across outreach projects" icon={<Users className="h-4 w-4" />} tone="brand" />
        <StatCard label="Emails sent" value={formatNumber(stats.totals.emailsSent)} sub={`${formatPercent(stats.totals.openRate)} open rate`} icon={<Send className="h-4 w-4" />} />
        <StatCard label="Replies received" value={formatNumber(stats.totals.repliesReceived)} sub={`${formatPercent(stats.totals.replyRate)} reply rate`} icon={<Inbox className="h-4 w-4" />} tone="positive" />
        <StatCard label="Emails to review" value={formatNumber(stats.totals.drafts)} sub={`${formatNumber(stats.totals.scheduled)} scheduled to send`} icon={<Mail className="h-4 w-4" />} tone={stats.totals.drafts > 0 ? 'warning' : 'neutral'} />
      </div>

      <Card className="mt-6 overflow-hidden">
        <CardHeader title="Your projects" description="Open a project or jump straight into your work."
          actions={<Link to="/projects" className="inline-flex min-h-9 items-center gap-1.5 text-xs font-medium text-brand hover:underline">View all <ArrowRight className="h-3.5 w-3.5" /></Link>} />
        {projectsError ? <CardBody><ErrorBlock error={projectsError} /></CardBody> : projectsLoading ? <LoadingBlock label="Loading projects…" /> : displayProjects.length ? (
          <div className="divide-y divide-line">
            {displayProjects.map((project) => (
              <div key={project.id} className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 px-5 py-4">
                <Link to={`/projects/${project.id}`} className="group flex min-w-0 flex-1 basis-56 items-center gap-3 rounded-lg">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand/[0.07] text-sm font-semibold text-brand">{initials(project.name)}</span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-semibold group-hover:text-brand">{project.name}</span>
                      <Badge tone="neutral">{project.kind === 'CONTENT' ? 'Content' : 'Outreach'}</Badge>
                      {project.status !== 'ACTIVE' && <Badge tone="warning">{titleCase(project.status)}</Badge>}
                    </div>
                    <p className="mt-1 text-xs text-muted">{project.kind === 'CONTENT'
                      ? `${formatNumber(project.stats?.topics ?? 0)} topics · ${formatNumber(project.stats?.posts ?? 0)} posts`
                      : `${formatNumber(project.stats?.leads ?? 0)} leads · ${formatNumber(project.stats?.emailsSent ?? 0)} emails sent`}</p>
                  </div>
                </Link>
                <div className="flex flex-wrap items-center gap-2">
                  <Link to={`/projects/${project.id}/${project.kind === 'CONTENT' ? 'topics' : 'leads'}`} className="inline-flex min-h-9 items-center rounded-lg px-3 text-xs font-medium text-muted hover:bg-raised hover:text-ink">{project.kind === 'CONTENT' ? 'Topics' : 'Leads'}</Link>
                  {project.kind !== 'CONTENT' && <Link to={`/projects/${project.id}/inbox`} className="inline-flex min-h-9 items-center rounded-lg px-3 text-xs font-medium text-muted hover:bg-raised hover:text-ink">Inbox</Link>}
                  <Link to={`/projects/${project.id}/${project.kind === 'CONTENT' ? 'posts' : 'emails'}?status=DRAFT`} className="inline-flex min-h-9 items-center gap-2 rounded-lg border border-line px-3 text-xs font-medium hover:border-brand/30 hover:bg-brand/5 hover:text-brand">{project.kind === 'CONTENT' ? 'Review posts' : 'Review emails'}<ArrowRight className="h-3.5 w-3.5" /></Link>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="px-5 py-5 text-[13px] text-muted">Your projects will appear here once created.</p>}
      </Card>

      <div className="mt-6 grid gap-5 lg:grid-cols-3">
        <Card className="flex flex-col lg:col-span-2">
          <CardHeader title="Outreach activity" description="Leads discovered and emails sent over the last 14 days" />
          <CardBody className="flex flex-1 flex-col justify-center py-7">
            <Sparkline data={stats.timeline} height={180} series={[
              { key: 'leads', label: 'Leads found', color: 'rgb(var(--brand))' },
              { key: 'emails', label: 'Emails sent', color: 'rgb(var(--positive))' },
            ]} />
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Lead pipeline" description="Current stage across all outreach projects" />
          <CardBody className="space-y-3">
            {FUNNEL.map((step) => {
              const count = stats.leadsByStatus[step.status] ?? 0;
              return <div key={step.status} className="grid grid-cols-[80px_1fr_36px] items-center gap-3 text-xs">
                <span className="text-muted">{step.label}</span>
                <Progress value={count} max={maxFunnel} />
                <span className="text-right font-medium tabular-nums">{formatNumber(count)}</span>
              </div>;
            })}
          </CardBody>
        </Card>
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Recent activity" description="The latest updates from your projects"
            actions={stats.jobsByStatus.PENDING ? <Badge tone="warning">{stats.jobsByStatus.PENDING} jobs pending</Badge> : undefined} />
          {activityError ? <CardBody><ErrorBlock error={activityError} /></CardBody> : activity?.length ? <div className="max-h-80 divide-y divide-line overflow-y-auto">
            {activity.map((event) => <div key={event.id} className="flex items-start gap-3 px-5 py-3.5">
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-raised text-muted"><Activity className="h-3.5 w-3.5" /></span>
              <div className="min-w-0 flex-1"><p className="text-[13px]">{event.message}</p><p className="mt-1 text-xs text-muted">{titleCase(event.type)}</p></div>
              <span className="shrink-0 text-xs text-faint">{formatRelative(event.createdAt)}</span>
            </div>)}
          </div> : <EmptyState title="No activity yet" description="Updates appear here as your agents research leads and create content." />}
        </Card>
        <Card>
          <CardHeader title="Research targets" description="Lead types and progress toward their targets" />
          {stats.leadsByType.length ? <CardBody className="space-y-5">
            {stats.leadsByType.slice(0, 7).map((type) => <div key={type.leadTypeId ?? 'none'}>
              <div className="mb-2 flex items-center justify-between gap-2 text-xs">
                <span className="truncate text-muted">{type.name}</span>
                <span className="shrink-0 font-medium tabular-nums">{formatNumber(type.count)}{type.targetCount ? <span className="font-normal text-faint"> / {formatNumber(type.targetCount)}</span> : null}</span>
              </div>
              <Progress value={type.count} max={type.targetCount || type.count || 1} color={type.color} />
            </div>)}
          </CardBody> : <EmptyState title="No research targets" description="Add lead types in an outreach project to guide your research agents." />}
        </Card>
      </div>
      <p className="mt-6 flex items-center gap-2 text-xs text-faint"><span className="h-1.5 w-1.5 rounded-full bg-positive" />Statistics refresh every 20 seconds · {activeProjects.length} active {activeProjects.length === 1 ? 'project' : 'projects'}</p>
    </Page>
  );
};
